import type { ButtonHTMLAttributes } from "react";
import { createUseStyles } from "react-jss";
import type { VetBoatsTheme } from "../../theme";

type Variant = "primary" | "secondary" | "danger" | "ghost";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "9px 16px",
    font: "inherit",
    fontWeight: 600,
    borderRadius: 6,
    border: "1px solid transparent",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
    "&:focus-visible": {
      outline: `2px solid ${theme.color.accent}`,
      outlineOffset: 2,
    },
  },
  small: { padding: "5px 10px", fontSize: 13 },
  primary: {
    background: theme.color.primary,
    color: "#fff",
    "&:hover:not(:disabled)": { background: theme.color.primaryHover },
  },
  secondary: {
    background: theme.color.surface,
    color: theme.color.primary,
    borderColor: theme.color.border,
    "&:hover:not(:disabled)": { borderColor: theme.color.primary },
  },
  danger: {
    background: theme.color.surface,
    color: theme.color.danger,
    borderColor: `${theme.color.danger}66`,
    "&:hover:not(:disabled)": { background: theme.color.dangerBg },
  },
  ghost: {
    background: "transparent",
    color: theme.color.primary,
    "&:hover:not(:disabled)": { background: `${theme.color.primary}0f` },
  },
}));

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  small?: boolean;
}

export const Button = ({
  variant = "primary",
  small = false,
  className,
  type = "button",
  ...rest
}: ButtonProps) => {
  const classes = useStyles();
  const classNames = [
    classes.button,
    classes[variant],
    small ? classes.small : "",
    className ?? "",
  ];
  return <button type={type} className={classNames.join(" ")} {...rest} />;
};
